import React, {useCallback, useEffect, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {Heading, Text} from '@albert-io/atomic';
import {callTargetedAction, setUpStore} from 'client/framework';

import FreeResponseStore from '../FreeResponseQuestion.store';
import freeResponseActions from '../FreeResponseQuestion.actions';
import type {WSStore} from '../FreeResponseQuestion.types';
import {
  useQuestionTranslationStore,
  NoTranslationWarningHeader,
  answerInEnglishText,
  translatedInstructions
} from '../../shared/TranslationSection';
import useDraftGuess from '../../../../AssignmentPracticeView/DraftGuesses/useDraftGuess';

import WrittenSubmissionContext from './WrittenSubmission.context';
import WrittenSubmissionInput from './Editor/WrittenSubmissionInput.react';

interface Props {
  // May be undefined while the question's store has not yet been set up; the
  // draft-guess/store lookups below tolerate a missing store name.
  storeName?: string;
  submitted?: boolean;
  question: any;
  /** Set where the response belongs to someone else — a teacher reading a student's submission. */
  isViewingStudentWork?: boolean;
}

const WrittenStudentSubmission = ({
  storeName,
  submitted = false,
  question,
  isViewingStudentWork = false
}: Props) => {
  const {showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );

  const {DraftGuessSaveStatus, startTimer} = useDraftGuess(storeName, submitted);

  const [store, setStore] = useState<null | WSStore>(null);

  const onChange = useCallback(
    (data) => {
      if (!submitted) {
        callTargetedAction({
          name: freeResponseActions.UPDATE_RESPONSE,
          payload: data,
          targetStore: storeName
        });
      }
    },
    [storeName, submitted]
  );

  useEffect(() => {
    // `setUpStore` returns the framework base `Store`; the FreeResponse store's
    // public surface is described by `WSStore`.
    const newStore = setUpStore(
      FreeResponseStore,
      storeName,
      question.getId(),
      question
    ) as unknown as WSStore;
    // oxlint-disable-next-line react/react-compiler -- store swap plus delayed remount workaround for a Slate cursor out-of-bounds bug (see comment below)
    setStore(null);

    let scrollLock: {container: Element; preventScroll: () => void} | null = null;
    let scrollLockFrame: number | null = null;

    // The releasing frame is throttled in background tabs, so a teardown between the timeout
    // firing and that frame would otherwise leave the listener pinning the shared question
    // area's scrollTop to 0. One owner releases it, whichever path gets there first.
    const releaseScrollLock = () => {
      if (scrollLockFrame !== null) {
        cancelAnimationFrame(scrollLockFrame);
        scrollLockFrame = null;
      }
      if (scrollLock) {
        scrollLock.container.removeEventListener('scroll', scrollLock.preventScroll);
        scrollLock = null;
      }
    };

    const timeout = setTimeout(() => {
      // hot swapping written submission input editor values causes a bug where if the previous cursor is out of bounds of the next
      // written submission, it causes an error. This only occurs when the next question is another written submission

      // FIX: Prevent unwanted scroll-to-textarea on initial page load.
      //
      // When navigating to an FRQ question from the subject guide, the page should
      // show the question prompt at the top. However, the Slate rich-text editor
      // renders a contenteditable div, and browsers natively auto-scroll overflow
      // containers to reveal newly-inserted contenteditable elements. This causes
      // the practice-view question area to jump down to the textarea on mount.
      //
      // We attach a scroll listener that synchronously resets scrollTop to 0,
      // preventing the browser's auto-scroll from ever being painted. The listener
      // is removed on the next frame once the contenteditable has stabilized.
      const container = document.querySelector('[data-testid="practice-view__question-area"]');
      const preventScroll = () => {
        if (container) container.scrollTop = 0;
      };
      if (container) {
        container.addEventListener('scroll', preventScroll);
        scrollLock = {container, preventScroll};
      }

      startTimer(); // start time_elapsed timer
      setStore(newStore);

      // Remove the scroll lock after the next frame so normal user scrolling
      // is not blocked. The browser's auto-scroll fires synchronously during
      // DOM insertion (same tick as setStore), so by the next animation frame
      // it has already been neutralized and the listener is no longer needed.
      scrollLockFrame = requestAnimationFrame(releaseScrollLock);
    }, 100);

    return () => {
      clearTimeout(timeout);
      releaseScrollLock();
    };
  }, [storeName, question, startTimer]);

  const editorState = store ? store.getValue() : null;
  const labelText = submitted
    ? isViewingStudentWork
      ? 'Student’s Response'
      : 'Your Response'
    : 'Provide Your Response Below';

  const Wrapper = showTranslations ? NoTranslationWarningHeader : React.Fragment;

  const wordCountText = showTranslations ? 'Conteo de palabras' : 'Word Count';

  return (
    <div className='mb-x4 w-full'>
      <Heading as='h2' size='s' id='wss-text-input'>
        {showTranslations ? translatedInstructions.freeResponse : labelText}
      </Heading>
      <Wrapper className='mb-x2' text={answerInEnglishText}>
        {store && editorState && (
          <WrittenSubmissionContext.Provider
            value={{store, storeName: storeName ?? null, submitted}}
          >
            <WrittenSubmissionInput
              onChange={onChange}
              value={editorState}
              readOnly={submitted}
              aria-labelledby='wss-text-input'
            />
            {!submitted ? (
              <span className='mt-x1 flex items-center'>
                <DraftGuessSaveStatus store={store} />
                <Text size='s' className='content-tertiary ml-auto'>
                  {wordCountText}: {store.getWordCount()}
                </Text>
              </span>
            ) : null}
          </WrittenSubmissionContext.Provider>
        )}
      </Wrapper>
    </div>
  );
};

export default WrittenStudentSubmission;
